import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { IdentificationBadge, Plus, Trash, PencilSimple, X, Check } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = { name: '', description: '', pages: [] };

const PersonaManagement = () => {
  const [personas, setPersonas] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [personasRes, catalogRes] = await Promise.all([
        axios.get(`${API}/personas`),
        axios.get(`${API}/personas/pages-catalog`),
      ]);
      setPersonas(personasRes.data);
      setCatalog(catalogRes.data);
    } catch (error) {
      console.error('Error fetching personas:', error);
      toast.error('Failed to load personas');
    } finally {
      setLoading(false);
    }
  };

  const togglePage = (pages, pageKey) =>
    pages.includes(pageKey) ? pages.filter((p) => p !== pageKey) : [...pages, pageKey];

  const createPersona = async () => {
    if (!form.name.trim()) {
      toast.error('Persona name is required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/personas`, form);
      toast.success('Persona created');
      setForm(emptyForm);
      setShowCreate(false);
      fetchData();
    } catch (error) {
      console.error('Error creating persona:', error);
      toast.error(error.response?.data?.detail || 'Failed to create persona');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (persona) => {
    setEditingId(persona.id);
    setEditForm({ name: persona.name, description: persona.description || '', pages: persona.pages || [] });
  };

  const saveEdit = async (id) => {
    if (!editForm.name.trim()) {
      toast.error('Persona name is required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(`${API}/personas/${id}`, editForm);
      toast.success('Persona updated');
      setEditingId(null);
      fetchData();
    } catch (error) {
      console.error('Error updating persona:', error);
      toast.error(error.response?.data?.detail || 'Failed to update persona');
    } finally {
      setSubmitting(false);
    }
  };

  const deletePersona = async (id) => {
    if (!window.confirm('Delete this persona? Users assigned to it will lose access until reassigned.')) {
      return;
    }
    try {
      await axios.delete(`${API}/personas/${id}`);
      toast.success('Persona deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting persona:', error);
      toast.error('Failed to delete persona');
    }
  };

  const pageLabel = (key) => catalog.find((p) => p.key === key)?.label || key;

  if (loading) {
    return (
      <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-40 bg-white border border-slate-200 rounded-lg skeleton" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12" data-testid="persona-management-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Persona Management
            </h1>
            <p className="text-base font-normal text-slate-600 leading-relaxed mt-2">
              Create personas and control which pages they can access
            </p>
          </div>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="flex items-center space-x-2 btn-primary px-5 py-2.5 rounded-md text-sm font-medium"
            data-testid="new-persona-button"
          >
            <Plus size={18} weight="bold" />
            <span>New Persona</span>
          </button>
        </div>

        {showCreate && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 mb-8" data-testid="create-persona-form">
            <h3 className="text-lg font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              New Persona
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Recruiter"
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                  data-testid="persona-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Description <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What this persona is for"
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                  data-testid="persona-description-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Pages</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {catalog.map((page) => (
                    <label
                      key={page.key}
                      className="flex items-center space-x-2 px-3 py-2 border border-slate-200 rounded-md text-sm cursor-pointer hover:bg-slate-50"
                      data-testid={`persona-page-checkbox-${page.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={form.pages.includes(page.key)}
                        onChange={() => setForm({ ...form, pages: togglePage(form.pages, page.key) })}
                        className="rounded border-slate-300 text-[#002FA7] focus:ring-[#002FA7]"
                      />
                      <span>{page.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center space-x-3 pt-2">
                <button
                  onClick={createPersona}
                  disabled={submitting}
                  className="btn-primary px-5 py-2 rounded-md text-sm font-medium disabled:opacity-50"
                  data-testid="save-persona-button"
                >
                  Create Persona
                </button>
                <button
                  onClick={() => { setShowCreate(false); setForm(emptyForm); }}
                  className="px-5 py-2 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {personas.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="empty-personas-state">
            <div className="inline-flex bg-blue-50 p-4 rounded-lg mb-4">
              <IdentificationBadge size={48} weight="duotone" className="text-[#002FA7]" />
            </div>
            <p className="text-lg font-medium text-slate-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              No personas yet
            </p>
            <p className="text-base text-slate-600">
              Create a persona to define which pages a group of users can access.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="personas-grid">
            {personas.map((persona) => (
              <div
                key={persona.id}
                className="bg-white border border-slate-200 rounded-lg p-6"
                data-testid={`persona-card-${persona.id}`}
              >
                {editingId === persona.id ? (
                  <div className="space-y-4">
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    />
                    <input
                      type="text"
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      placeholder="Description"
                      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      {catalog.map((page) => (
                        <label key={page.key} className="flex items-center space-x-2 px-3 py-2 border border-slate-200 rounded-md text-sm cursor-pointer hover:bg-slate-50">
                          <input
                            type="checkbox"
                            checked={editForm.pages.includes(page.key)}
                            onChange={() => setEditForm({ ...editForm, pages: togglePage(editForm.pages, page.key) })}
                            className="rounded border-slate-300 text-[#002FA7] focus:ring-[#002FA7]"
                          />
                          <span>{page.label}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => saveEdit(persona.id)}
                        disabled={submitting}
                        className="flex items-center space-x-1 btn-primary px-4 py-1.5 rounded-md text-sm font-medium disabled:opacity-50"
                        data-testid={`save-edit-persona-${persona.id}`}
                      >
                        <Check size={16} />
                        <span>Save</span>
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="flex items-center space-x-1 px-4 py-1.5 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100"
                      >
                        <X size={16} />
                        <span>Cancel</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between mb-3">
                      <div className="bg-blue-50 p-2.5 rounded-md">
                        <IdentificationBadge size={20} weight="duotone" className="text-[#002FA7]" />
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => startEdit(persona)}
                          className="p-1.5 text-slate-400 hover:text-[#002FA7] hover:bg-blue-50 rounded transition-colors"
                          data-testid={`edit-persona-${persona.id}`}
                          title="Edit"
                        >
                          <PencilSimple size={16} />
                        </button>
                        <button
                          onClick={() => deletePersona(persona.id)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          data-testid={`delete-persona-${persona.id}`}
                          title="Delete"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {persona.name}
                    </h3>
                    {persona.description && (
                      <p className="text-sm text-slate-600 mb-3">{persona.description}</p>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {(persona.pages || []).length === 0 ? (
                        <span className="text-xs text-slate-400">No pages assigned</span>
                      ) : (
                        persona.pages.map((key) => (
                          <span key={key} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                            {pageLabel(key)}
                          </span>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonaManagement;
